/**
 * Base error class for all EventMsg-related errors.
 * Provides consistent error handling with context and solutions.
 */
export class EventMsgError extends Error {
  /** Error code for programmatic handling */
  code: string;

  /** Additional context about the error */
  readonly context: Record<string, unknown> | undefined;

  /** Suggested solutions for the error */
  readonly solutions: string[];

  /** Original error that caused this error */
  readonly cause: Error | undefined;

  constructor(
    message: string,
    code: string,
    options: {
      context?: Record<string, unknown>;
      solutions?: string[];
      cause?: Error;
    } = {}
  ) {
    super(message);

    this.name = this.constructor.name;
    this.code = code;
    this.context = options.context;
    this.solutions = options.solutions || [];
    this.cause = options.cause;

    // Maintain proper stack trace in V8
    if (Error.captureStackTrace) {
      Error.captureStackTrace(this, this.constructor);
    }
  }

  /**
   * Get a detailed error message including context and solutions
   */
  getDetailedMessage(): string {
    let details = `${this.name}: ${this.message}`;

    if (this.code) {
      details += ` (${this.code})`;
    }

    if (this.context && Object.keys(this.context).length > 0) {
      details += '\nContext:';
      for (const [key, value] of Object.entries(this.context)) {
        details += `\n  ${key}: ${JSON.stringify(value)}`;
      }
    }

    if (this.solutions.length > 0) {
      details += '\nSuggested solutions:';
      for (const solution of this.solutions) {
        details += `\n  • ${solution}`;
      }
    }

    if (this.cause) {
      details += `\nCaused by: ${this.cause.message}`;
    }

    return details;
  }

  /**
   * Convert error to JSON for logging/serialization
   */
  toJSON(): Record<string, unknown> {
    return {
      name: this.name,
      message: this.message,
      code: this.code,
      context: this.context,
      solutions: this.solutions,
      stack: this.stack,
      cause: this.cause
        ? {
            name: this.cause.name,
            message: this.cause.message,
          }
        : undefined,
    };
  }
}
